import { useState } from 'react';
import { Bell, Calendar, Check, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Logo } from '@/components/layout/logo';
import { FloatingField, FloatingTextarea } from '@/components/ui/floating-field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { CurrencyInput } from '@/components/ui/currency-input';
import { TaskCheckbox } from '@/components/shared/task-checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonCard } from '@/components/ui/skeleton-card';
import { UserAvatar } from '@/components/users/user-avatar';
import { todayISO } from '@/lib/dates';

export function DevUiKitPage() {
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState<string | null>('22:00');
  const [seg, setSeg] = useState<'a' | 'b' | 'c'>('a');
  const [checked, setChecked] = useState(false);
  const [value, setValue] = useState<number | null>(null);

  return (
    <div className="mx-auto max-w-3xl space-y-10 p-6">
      <header>
        <Eyebrow>#dev · ui kit</Eyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Componentes</h1>
      </header>

      <Section title="Logo">
        <div className="space-y-2">
          <Logo size="sm" />
          <Logo size="md" />
          <Logo size="lg" />
        </div>
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap gap-2">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="link">Link</Button>
        </div>
      </Section>

      <Section title="Badges">
        <div className="flex flex-wrap gap-2">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="destructive">Destructive</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="soft">Soft</Badge>
          <Badge variant="success">Success</Badge>
          <Badge variant="warning">Warning</Badge>
        </div>
      </Section>

      <Section title="Avatares">
        <div className="flex items-center gap-3">
          <UserAvatar name="Leo Garcez" colorHex="#E10A17" size="sm" />
          <UserAvatar name="Pedro Souza" colorHex="#3B82F6" size="md" />
          <UserAvatar name="Kelvyn Lima" colorHex="#F59E0B" size="lg" />
          <UserAvatar name="Underline" colorHex="#111" size="xl" />
        </div>
      </Section>

      <Section title="Floating fields">
        <div className="space-y-3 max-w-md">
          <FloatingField label="Nome" placeholder="Underline" />
          <FloatingField label="Email" type="email" error="Email inválido" />
          <FloatingTextarea label="Briefing" />
        </div>
      </Section>

      <Section title="Currency / Date / Time">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
          <CurrencyInput label="Couvert" value={value} onChange={setValue} />
          <div>
            <p className="mono uppercase tracking-wider text-[10px] text-muted-foreground mb-1.5">
              Data
            </p>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div>
            <p className="mono uppercase tracking-wider text-[10px] text-muted-foreground mb-1.5">
              Hora
            </p>
            <TimePicker value={time} onChange={setTime} />
          </div>
        </div>
      </Section>

      <Section title="Segmented Control">
        <SegmentedControl
          value={seg}
          onChange={setSeg}
          options={[
            { value: 'a', label: 'Free', icon: <Check className="h-3 w-3" /> },
            { value: 'b', label: 'Free até', icon: <Bell className="h-3 w-3" /> },
            { value: 'c', label: 'Pago', icon: <Megaphone className="h-3 w-3" /> },
          ]}
          size="sm"
          className="max-w-md"
        />
      </Section>

      <Section title="Task checkbox">
        <TaskCheckbox checked={checked} onToggle={() => setChecked((v) => !v)} size={28} />
      </Section>

      <Section title="Empty state">
        <EmptyState
          icon={<Calendar className="h-5 w-5" />}
          title="Sem eventos"
          description="Crie o primeiro evento."
        />
      </Section>

      <Section title="Skeleton">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="eyebrow mb-3">{title}</p>
      {children}
    </section>
  );
}
